'use client'

import React, { useEffect, useState, useRef, useCallback } from 'react'
import ArticleDetail, { type ArticleNews } from '@/components/ArticleDetail'
import { ArticleBlockSkeleton } from '@/components/PageSkeletons'
import { getApiBaseUrl } from '@/lib/config'

type News = ArticleNews

interface CurrentNews {
  id: number
  title: string
  slug: string
  image: string | null
  category: {
    id: number
    title: string
    slug: string
  } | null
}

interface NextNewsInfiniteScrollProps {
  categoryId: number
  excludeId: number
  categorySlug: string
  currentNewsId: number
  currentNews: CurrentNews
  currentNewsIsType?: number | null
}

function pushArticleHistory(pathname: string) {
  window.history.pushState({ path: pathname }, '', pathname)
}

export default function NextNewsInfiniteScroll({
  categoryId,
  excludeId,
  categorySlug,
  currentNewsId,
  currentNews,
  currentNewsIsType,
}: NextNewsInfiniteScrollProps) {
  const [newsList, setNewsList] = useState<Array<{ id: number; slug: string }>>([])
  const [loadedNews, setLoadedNews] = useState<Map<number, News>>(new Map())
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingNext, setIsLoadingNext] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const bottomSentinelRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef(false)
  const fetchingPagesRef = useRef<Set<number>>(new Set())
  const lastUrlRef = useRef<string>('')
  const lastActiveArticleIdRef = useRef<number | null>(null)
  const bottomObserverRef = useRef<IntersectionObserver | null>(null)
  const scrollPositionRef = useRef<number>(0)
  const previousScrollHeightRef = useRef<number>(0)
  const articleRefs = useRef<Map<number, HTMLElement>>(new Map())
  const isInitialLoadRef = useRef<boolean>(true)
  const loadedNewsRef = useRef<Map<number, News>>(new Map())
  const prefetchSidebarIdsRef = useRef<Set<number>>(new Set())

  useEffect(() => {
    loadedNewsRef.current = loadedNews
  }, [loadedNews])


  // Fetch news list
  const fetchNewsList = useCallback(async (pageNum: number) => {
    if (fetchingPagesRef.current.has(pageNum)) {
      return false
    }
    
    fetchingPagesRef.current.add(pageNum)
    
    try {
      const apiUrl = getApiBaseUrl()
      const isTypeParam = currentNewsIsType !== undefined && currentNewsIsType !== null ? `&isType=${currentNewsIsType}` : ''
      const response = await fetch(
        `${apiUrl}/api/news/category/${categoryId}?page=${pageNum}&exclude=${excludeId}&limit=10${isTypeParam}`
      )
      
      if (!response.ok) {
        throw new Error('Failed to fetch news list')
      }
      
      const data = await response.json()

      if (data.news && data.news.length > 0) {
        setNewsList((prev) => {
          const existingIds = new Set(prev.map((n) => n.id))
          const newNews = data.news.filter((n: { id: number }) => !existingIds.has(n.id))
          return [...prev, ...newNews.map((n: { id: number; slug: string }) => ({ id: n.id, slug: n.slug }))]
        })
        setHasMore(data.hasMore)
        setPage(pageNum + 1)
        return true
      } else {
        setHasMore(false)
        return false
      }
    } catch (error) {
      console.error('Error fetching news list:', error)
      setHasMore(false)
      return false
    } finally {
      fetchingPagesRef.current.delete(pageNum)
    }
  }, [categoryId, excludeId, currentNewsIsType])

  // Fetch batch news details - single API call for multiple news
  const fetchBatchNewsDetails = useCallback(async (newsIds: number[]): Promise<News[]> => {
    if (newsIds.length === 0) return []
    
    const apiUrl = getApiBaseUrl()
    
    try {
      // Single API call for all news items
      const response = await fetch(`${apiUrl}/api/news/detail/batch?ids=${newsIds.join(',')}`)
      if (!response.ok) {
        return []
      }
      const result = await response.json()
      return result.data || []
    } catch (error) {
      console.error('Error fetching batch news details:', error)
      return []
    }
  }, [])

  /** Sidebar: listede sıradaki var ama detay map'te yoksa batch ile çek + nextNewsPrefetched */
  const prefetchNextForSidebar = useCallback(
    (currentIndex: number) => {
      const list = newsList
      if (currentIndex < 0 || currentIndex >= list.length - 1) return
      const nextMeta = list[currentIndex + 1]
      if (!nextMeta?.id) return
      if (loadedNewsRef.current.has(nextMeta.id)) return
      if (prefetchSidebarIdsRef.current.has(nextMeta.id)) return
      prefetchSidebarIdsRef.current.add(nextMeta.id)
      fetchBatchNewsDetails([nextMeta.id])
        .then((arr) => {
          prefetchSidebarIdsRef.current.delete(nextMeta.id)
          const n = arr[0]
          if (!n) return
          setLoadedNews((prev) => {
            const m = new Map(prev)
            m.set(n.id, n)
            return m
          })
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('nextNewsPrefetched', { detail: n }))
          }
        })
        .catch(() => {
          prefetchSidebarIdsRef.current.delete(nextMeta.id)
        })
    },
    [newsList, fetchBatchNewsDetails],
  )

  // Initial load - fetch news list
  useEffect(() => {
    setIsLoading(true)
    setNewsList([])
    setLoadedNews(new Map())
    setVisibleRange({ start: 0, end: 0 })
    setPage(1)
    setHasMore(true)
    fetchingPagesRef.current.clear()
    lastUrlRef.current = ''
    isInitialLoadRef.current = true
    previousScrollHeightRef.current = 0
    
    const loadInitial = async () => {
      // Extract slug from current URL for the first news
      let currentSlug = ''
      if (typeof window !== 'undefined') {
        const path = window.location.pathname
        const parts = path.split('-')
        if (parts.length > 1) {
          currentSlug = parts.slice(0, -1).join('-')
        }
      }
      
      // Add current news to the list first
      setNewsList([{ id: currentNewsId, slug: currentSlug || categorySlug }])
      
      const success = await fetchNewsList(1)
      if (!success) {
        setIsLoading(false)
      }
    }
    
    loadInitial()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, excludeId, currentNewsId, categorySlug])

  // Load first batch of news details when list is ready (5 items, excluding current news)
  useEffect(() => {
    if (newsList.length > 0 && loadedNews.size === 0 && !isFetchingRef.current) {
        setIsLoading(true)
        isFetchingRef.current = true
        
        // Fetch first 5 news details in batch (excluding current news - it's already shown in page)
        const batchSize = 3
        const newsIdsToLoad = newsList
          .slice(1) // Skip first item (currentNewsId)
          .slice(0, batchSize)
          .map(item => item.id)
        
        fetchBatchNewsDetails(newsIdsToLoad).then((newsArray) => {
          if (newsArray.length > 0) {
            // Don't save scroll position on initial load - it will cause unwanted scroll
            if (typeof window !== 'undefined' && !isInitialLoadRef.current) {
              previousScrollHeightRef.current = document.documentElement.scrollHeight
              scrollPositionRef.current = window.scrollY
            }
            
            // Store first next news for event dispatch (find news matching first ID in newsIdsToLoad)
            const firstNextNewsId = newsIdsToLoad[0]
            const firstNextNews = newsArray.find(n => n.id === firstNextNewsId) || null
            
            // Add all fetched news to loadedNews map
            setLoadedNews((prev) => {
              const newMap = new Map(prev)
              newsArray.forEach(news => {
                newMap.set(news.id, news)
              })
              return newMap
            })
            
            // Don't set visible range yet - wait for user to scroll down
            // visibleRange stays at { start: 0, end: 0 } until scroll triggers
            
            // Dispatch event after state update to update sidebar with initial next news
            if (typeof window !== 'undefined' && firstNextNews) {
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('newsChanged', { 
                  detail: {
                    current: currentNews,
                    prev: null,
                    next: firstNextNews
                  }
                }))
              }, 0)
            }
            
            // Mark initial load as complete
            isInitialLoadRef.current = false
          }
          
          setIsLoading(false)
          isFetchingRef.current = false
        }).catch((error) => {
          console.error('Error loading batch news:', error)
          setIsLoading(false)
          isFetchingRef.current = false
          isInitialLoadRef.current = false
        })
    }
  }, [newsList, loadedNews.size, fetchBatchNewsDetails])

  // Update URL based on scroll position (which article is most visible)
  useEffect(() => {
    if (newsList.length === 0 || loadedNews.size === 0 || typeof window === 'undefined') return

    const updateActiveArticle = () => {
      let activeArticleId: number | null = null
      let minDistance = Infinity

      // Check main-article (first news) first
      const mainArticle = document.querySelector('#main-article') as HTMLElement
      if (mainArticle) {
        const rect = mainArticle.getBoundingClientRect()
        // Check if main-article is in viewport
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          const distance = Math.abs(rect.top)
          if (distance < minDistance) {
            minDistance = distance
            activeArticleId = currentNewsId
          }
        }
      }

      // Check articles from infinite scroll
      articleRefs.current.forEach((articleEl, id) => {
        const rect = articleEl.getBoundingClientRect()
        // Check if article is in viewport
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          // Distance from top of viewport
          const distance = Math.abs(rect.top)
          if (distance < minDistance) {
            minDistance = distance
            activeArticleId = id
          }
        }
      })

      if (activeArticleId) {
        const articleChanged = lastActiveArticleIdRef.current !== activeArticleId

        // If main-article is active, dispatch event with currentNewsId
        if (activeArticleId === currentNewsId) {
          const newUrl = `/${currentNews.category?.slug || categorySlug}/${currentNews.slug}-${currentNews.id}`
          const currentPath = window.location.pathname
          const urlChanged = newUrl !== currentPath && newUrl !== lastUrlRef.current

          if (urlChanged) {
            lastUrlRef.current = newUrl
            pushArticleHistory(newUrl)
          }

          // URL aynı kalsa bile aktif haber değişince sidebar + reklam güncellensin
          if (articleChanged || urlChanged) {
            lastActiveArticleIdRef.current = activeArticleId
            const nextNews = newsList.length > 1 ? loadedNews.get(newsList[1].id) : null
            window.dispatchEvent(new CustomEvent('newsChanged', {
              detail: {
                current: currentNews,
                prev: null,
                next: nextNews,
              },
            }))
          }
        } else {
          const activeNewsData = loadedNews.get(activeArticleId)
          if (activeNewsData) {
            const newUrl = `/${activeNewsData.category?.slug || categorySlug}/${activeNewsData.slug}-${activeNewsData.id}`
            const currentPath = window.location.pathname
            const urlChanged = newUrl !== currentPath && newUrl !== lastUrlRef.current

            if (urlChanged) {
              lastUrlRef.current = newUrl
              pushArticleHistory(newUrl)
            }

            if (articleChanged || urlChanged) {
              lastActiveArticleIdRef.current = activeArticleId
              const currentIndex = newsList.findIndex(n => n.id === activeArticleId)
              const prevNews = currentIndex === 1
                ? currentNews
                : currentIndex > 1
                  ? loadedNews.get(newsList[currentIndex - 1].id)
                  : null
              const nextNews = currentIndex >= 0 && currentIndex < newsList.length - 1
                ? loadedNews.get(newsList[currentIndex + 1].id)
                : null

              window.dispatchEvent(new CustomEvent('newsChanged', {
                detail: {
                  current: activeNewsData,
                  prev: prevNews,
                  next: nextNews,
                },
              }))
            }
          }
        }
      }

      /* Sidebar: URL güncellenmese bile listede sıradaki id var ve detay yoksa önden yükle */
      if (activeArticleId) {
        const idx =
          activeArticleId === currentNewsId
            ? 0
            : newsList.findIndex((n) => n.id === activeArticleId)
        if (
          idx >= 0 &&
          idx < newsList.length - 1 &&
          !loadedNews.get(newsList[idx + 1].id)
        ) {
          prefetchNextForSidebar(idx)
        }
      }
    }

    // Throttle scroll events
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateActiveArticle()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    // Initial check
    updateActiveArticle()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [newsList, loadedNews, categorySlug, currentNewsId, currentNews, prefetchNextForSidebar])

  // Restore scroll position after news loads
  useEffect(() => {
    if (loadedNews.size === 0 || isLoading || isLoadingNext) return
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof window !== 'undefined' && previousScrollHeightRef.current > 0) {
          const newScrollHeight = document.documentElement.scrollHeight
          const heightDifference = newScrollHeight - previousScrollHeightRef.current
          
          if (heightDifference > 0) {
            // Maintain scroll position
            window.scrollTo({
              top: scrollPositionRef.current,
              behavior: 'auto'
            })
          }
          
          previousScrollHeightRef.current = 0
        }
      })
    })
  }, [loadedNews.size, isLoading, isLoadingNext])

  // Bottom observer - load next news
  useEffect(() => {
    if (!bottomSentinelRef.current || newsList.length === 0) return

    if (bottomObserverRef.current) {
      bottomObserverRef.current.disconnect()
    }

    bottomObserverRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry.isIntersecting || isLoading || isLoadingNext || isFetchingRef.current) {
          return
        }
        
        const currentEnd = visibleRange.end
        // If visibleRange.end is 0, next index should be 1 (first news after current)
        // newsList[0] is currentNews, newsList[1] is first next news
        const nextIndex = currentEnd === 0 ? 1 : currentEnd + 1
        
        if (nextIndex < newsList.length) {
          const nextNews = newsList[nextIndex]
          if (nextNews && loadedNews.has(nextNews.id)) {
            // Already loaded, just update visible range
            setVisibleRange(prev => ({ ...prev, start: 1, end: nextIndex }))
          } else if (nextNews && !loadedNews.has(nextNews.id)) {
            setIsLoadingNext(true)
            isFetchingRef.current = true
            
            // Batch loading: load up to 5 news items starting from nextIndex
            const batchSize = 3
            const newsIdsToLoad = newsList
              .slice(nextIndex, Math.min(nextIndex + batchSize, newsList.length))
              .filter(item => !loadedNews.has(item.id))
              .map(item => item.id)
            
            if (newsIdsToLoad.length > 0) {
              fetchBatchNewsDetails(newsIdsToLoad).then((newsArray) => {
                if (newsArray.length > 0) {
                  setLoadedNews((prev) => {
                    const newMap = new Map(prev)
                    newsArray.forEach(news => {
                      newMap.set(news.id, news)
                    })
                    return newMap
                  })
                  
                  // Update visible range to show the first loaded news
                  setVisibleRange(prev => ({ ...prev, end: nextIndex }))
                  
                  // Dispatch event for the first news in the batch
                  const firstNews = newsArray[0]
                  if (firstNews && typeof window !== 'undefined') {
                    const newUrl = `/${firstNews.category?.slug || categorySlug}/${firstNews.slug}-${firstNews.id}`
                    const currentPath = window.location.pathname
                    
                    if (newUrl !== currentPath && newUrl !== lastUrlRef.current) {
                      lastUrlRef.current = newUrl
                      
                      // Find prev/next from newsList
                      const currentIndex = newsList.findIndex(n => n.id === firstNews.id)
                      const prevNews = currentIndex > 0 ? loadedNews.get(newsList[currentIndex - 1].id) || newsArray.find(n => n.id === newsList[currentIndex - 1].id) || null : null
                      const nextNews = currentIndex >= 0 && currentIndex < newsList.length - 1 
                        ? newsArray.find(n => n.id === newsList[currentIndex + 1].id) || loadedNews.get(newsList[currentIndex + 1].id) || null
                        : null
                      
                      pushArticleHistory(newUrl)
                      window.dispatchEvent(new CustomEvent('newsChanged', { 
                        detail: {
                          current: firstNews,
                          prev: prevNews,
                          next: nextNews
                        }
                      }))
                    }
                  }
                }
                
                setIsLoadingNext(false)
                isFetchingRef.current = false
              }).catch((error) => {
                console.error('Error loading batch news:', error)
                setIsLoadingNext(false)
                isFetchingRef.current = false
              })
            } else {
              // No more news to load in current list, check if we need more from API
              if (hasMore && !fetchingPagesRef.current.has(page)) {
                fetchNewsList(page).then(() => {
                  setIsLoadingNext(false)
                  isFetchingRef.current = false
                })
              } else {
                setIsLoadingNext(false)
                isFetchingRef.current = false
              }
            }
          }
        } else if (hasMore && !fetchingPagesRef.current.has(page)) {
          // Fetch more news from API
          setIsLoadingNext(true)
          isFetchingRef.current = true
          fetchNewsList(page).then(() => {
            setIsLoadingNext(false)
            isFetchingRef.current = false
          })
        }
        // If no hasMore and we've reached the end, do nothing (no more loading)
      },
      { threshold: 0.5, rootMargin: '100px' }
    )

    bottomObserverRef.current.observe(bottomSentinelRef.current)

    return () => {
      if (bottomObserverRef.current) {
        bottomObserverRef.current.disconnect()
      }
    }
  }, [visibleRange, newsList, hasMore, page, isLoading, isLoadingNext, loadedNews, fetchBatchNewsDetails, fetchNewsList, categorySlug])

  // Render single article — same UI as main article page
  const renderArticle = (news: News) => (
    <ArticleDetail
      key={news.id}
      ref={(el) => {
        if (el) articleRefs.current.set(news.id, el)
        else articleRefs.current.delete(news.id)
      }}
      news={news}
      categorySlug={categorySlug}
      slugString={`${news.slug}-${news.id}`}
      showComments={false}
      className="mb-10 w-full min-w-0 max-w-full border-t border-gray-200 pt-6 dark:border-slate-700 sm:mb-12 sm:pt-8"
    />
  )

  // Loading state
  if (isLoading && loadedNews.size === 0) {
    return (
      <section
        className="mt-8 w-full min-w-0 max-w-full sm:mt-10"
        aria-label="Sonraki Haberler"
        aria-busy="true"
      >
        <ArticleBlockSkeleton />
      </section>
    )
  }

  if (loadedNews.size === 0) {
    return null
  }

  // Get visible news articles
  const visibleNews: News[] = []
  for (let i = visibleRange.start; i <= visibleRange.end && i < newsList.length; i++) {
    const newsId = newsList[i].id
    if (newsId === excludeId) continue
    const news = loadedNews.get(newsId)
    if (news) {
      visibleNews.push(news)
    }
  }

  return (
    <section className="mt-8 w-full min-w-0 sm:mt-10" aria-label="Sonraki Haberler">
      {/* Render visible news articles */}
      {visibleNews.map((news) => renderArticle(news))}

      {isLoadingNext && (
        <div className="mt-8">
          <ArticleBlockSkeleton />
        </div>
      )}

      {/* Bottom sentinel for next news - only show if NOT loading and there's more to load */}
      {!isLoadingNext && !isFetchingRef.current && (hasMore || visibleRange.end + 1 < newsList.length) && (
        <div ref={bottomSentinelRef} className="h-10" />
      )}
    </section>
  )
}
